import {
  createUUID,
  EllipsisMenu,
  EllipsisMenuItem,
  LoadingIndicator,
} from '@vrplatform/components-legacy';
import {
  type OwnerStatementSummariesDetailData,
  type OwnerStatementSummariesDetailTableRow,
  type OwnerStatementSummariesDetailTableSection,
  useOwnerStatementSummariesDetailQuery,
} from './useOwnerStatementSummariesDetailQuery';
import { Collapse, Tag, ViewPlaceholder } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import {
  LazyTable,
  type MRT_Row,
  type MRT_ColumnDef,
  type MRT_ColumnPinningState,
} from '@vrplatform/table';
import { type api, useDashboard, useTeam } from '@vrplatform/data';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  type OwnerStatementSummariesDetailFilter as DetailFilter,
  useOwnerStatementSummariesDetailFilter,
} from './useOwnerStatementSummariesDetailFilter';
import { day, formatCurrency, toTitleCase } from '@vrplatform/utils';
import {
  ActionIcon,
  Box,
  Modal,
  Progress,
  ScrollArea,
  Stack,
  Text,
  Tabs,
  useMantineTheme,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { pluralize } from '@vrplatform/ui-common';
import {
  OwnerStatementSummariesDetailFilter,
  ownerStatementSummariesMonthRangeLabelFormatter,
} from './OwnerStatementSummariesDetailFilter';
import type { EmotionSx } from '@mantine/emotion';
import { StatementSummaryHeader } from './StatementSummaryHeader';
import { useOwnerStatementSummariesDetailTransactionsQuery } from './useOwnerStatementSummariesDetailTransactionsQuery';
import {
  augmentOwnerStatementSummariesDetailDataWithTransactions,
  getOwnerStatementSummariesDetailDisplayTitle,
  getOwnerStatementSummariesDetailFirstColumnHeader,
  getOwnerStatementSummariesDetailPrimaryColumnTitle,
  getOwnerStatementSummariesDetailSectionColumns,
  getOwnerStatementSummariesDetailTransactionLookup,
  getOwnerStatementSummariesDetailVisibleColumns,
} from './OwnerStatementSummariesDetail.utils';

export type OwnerStatementsModalFilter = {
  dateRange?: string;
  listingIds?: string;
  ownerIds?: string;
  periodIds?: string;
};

export type OwnerStatementSummariesJournalEntriesArgs = {
  filter: api['/reports/journal-entries']['get']['parameters']['query'];
};

export const OwnerStatementSummariesDetail = ({
  onOpenJournalEntries,
  onOpenListing,
  onOpenPayout,
  renderOwnerStatementsTable,
}: {
  onOpenJournalEntries?: (
    args: OwnerStatementSummariesJournalEntriesArgs
  ) => void;
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
  renderOwnerStatementsTable?: (
    modalFilter: OwnerStatementsModalFilter | undefined
  ) => React.ReactNode;
}) => {
  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  const queryData = useOwnerStatementSummariesDetailQuery();

  const hideBookingChannel =
    isOwnerPortal && !!queryData.data?.layouts?.[0]?.hideBookingChannel;

  const hideFilter = isOwnerPortal && !queryData.data;

  return (
    <Stack gap="md">
      {!hideFilter && (
        <OwnerStatementSummariesDetailFilter
          hideBookingChannel={hideBookingChannel}
        />
      )}
      <Content
        onOpenJournalEntries={onOpenJournalEntries}
        onOpenListing={onOpenListing}
        onOpenPayout={onOpenPayout}
        renderOwnerStatementsTable={renderOwnerStatementsTable}
      />
    </Stack>
  );
};

const Content = ({
  onOpenJournalEntries,
  onOpenListing,
  onOpenPayout,
  renderOwnerStatementsTable,
}: {
  onOpenJournalEntries?: (
    args: OwnerStatementSummariesJournalEntriesArgs
  ) => void;
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
  renderOwnerStatementsTable?: (
    modalFilter: OwnerStatementsModalFilter | undefined
  ) => React.ReactNode;
}) => {
  const queryData = useOwnerStatementSummariesDetailQuery();
  const { filter } = useOwnerStatementSummariesDetailFilter();
  const [openedOwnerStatementsModal, setOpenedOwnerStatementsModal] = useState<
    OwnerStatementsModalFilter | undefined
  >(undefined);
  const openOwnerStatementsModal = useCallback(
    (modalFilter: OwnerStatementsModalFilter) => {
      setOpenedOwnerStatementsModal(modalFilter);
    },
    []
  );

  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';
  const renderView = queryData.data?.resolvedView ?? filter.view;
  const transactionLookup = useMemo(() => {
    if (!queryData.data) {
      return {
        ids: [],
        shortRefs: [],
        uniqueRefs: [],
      };
    }

    return getOwnerStatementSummariesDetailTransactionLookup({
      data: queryData.data,
      view: renderView,
    });
  }, [queryData.data, renderView]);
  const transactionDetailsQuery =
    useOwnerStatementSummariesDetailTransactionsQuery({
      lookup: transactionLookup,
    });
  const hasTransactionLookup =
    transactionLookup.ids.length > 0 ||
    transactionLookup.shortRefs.length > 0 ||
    transactionLookup.uniqueRefs.length > 0;

  if (queryData.isLoading) {
    return <LoadingIndicator isFullPageLoading />;
  }

  if (queryData.error) {
    return (
      <ViewPlaceholder
        icon="AlertTriangleIcon"
        iconColor="red"
        title={'We failed to load the statement summary'}
        description={
          queryData.error.message ||
          'Please try again and contact our customer support if the issue persists.'
        }
      />
    );
  }

  if (!queryData.data) {
    return (
      <ViewPlaceholder
        icon="CommentQuestionIcon"
        title={'We could not find any data'}
        description={
          isOwnerPortal
            ? `No statements summary available for ${ownerStatementSummariesMonthRangeLabelFormatter(filter.monthRange)}`
            : `No published statements just yet for this owner in ${ownerStatementSummariesMonthRangeLabelFormatter(filter.monthRange)}`
        }
      />
    );
  }

  if (hasTransactionLookup && transactionDetailsQuery.isLoading) {
    return <LoadingIndicator isFullPageLoading />;
  }

  if (hasTransactionLookup && transactionDetailsQuery.error) {
    return (
      <ViewPlaceholder
        icon="AlertTriangleIcon"
        iconColor="red"
        title={'We failed to load the statement summary'}
        description={
          transactionDetailsQuery.error.message ||
          'Please try again and contact our customer support if the issue persists.'
        }
      />
    );
  }

  const detailData =
    hasTransactionLookup && transactionDetailsQuery.data
      ? augmentOwnerStatementSummariesDetailDataWithTransactions({
          data: queryData.data,
          transactionsById: transactionDetailsQuery.data,
          view: renderView,
        })
      : queryData.data;

  const currentLayout = detailData.layouts?.find(
    (layout) => layout.layoutId === queryData.layoutId
  );

  if (
    currentLayout?.netRevenueSection?.aggregate &&
    filter.view === 'reservation'
  ) {
    return (
      <ViewPlaceholder
        icon="FunctionsIcon"
        iconColor="gray"
        title={'"By Reservation" is not available'}
        description={
          'The current layout is using aggregated net revenue, so the "By Reservation" view is not available'
        }
      />
    );
  }

  return (
    <Box>
      <Progress
        value={100}
        striped
        animated
        mb="-xs"
        styles={{
          root: {
            opacity:
              queryData.isFetching || transactionDetailsQuery.isFetching
                ? '1'
                : '0',
            transition: 'opacity 400ms ease',
          },
        }}
      />
      <Stack gap={0}>
        <Header
          contact={detailData.contact}
          team={detailData.team}
          monthRange={filter.monthRange}
          currency={detailData.currency}
          financials={detailData.financials}
          listing={detailData.listing}
          onOpenListing={onOpenListing}
          onOpenPayout={onOpenPayout}
        />

        <OwnerStatementsModal
          modalFilter={openedOwnerStatementsModal}
          closeModal={() => setOpenedOwnerStatementsModal(undefined)}
          renderOwnerStatementsTable={renderOwnerStatementsTable}
        />

        <Tabs
          value={queryData.layoutId}
          onChange={(value) => value && queryData.setLayoutId(value)}
          variant="pills"
          styles={(theme) => ({
            panel: {
              marginTop: theme.spacing.md,
            },
            tab: {
              '&': {
                textAlign: 'left',
                justifyContent: 'flex-start',
                flexDirection: 'row',
                padding: '0.7rem 0.75rem',
                border: theme.other.border.button,
                boxShadow: theme.shadows.xs,
                '&[data-active="true"]': {
                  borderColor: theme.colors[theme.primaryColor][6],
                  color: `${theme.colors[theme.primaryColor][7]}`,
                  '&:hover': {
                    backgroundColor: theme.colors.neutral[0],
                  },
                  path: {
                    stroke: `${theme.colors[theme.primaryColor][7]}`,
                  },
                },
                '&:hover, &[data-active="true"]': {
                  backgroundColor: theme.colors.neutral[0],
                },
              },
            },
          })}
        >
          {detailData.layouts.length > 1 && (
            <Tabs.List>
              {detailData.layouts.map((layout) => (
                <Tabs.Tab
                  key={`${layout.layoutId}-tab`}
                  value={layout.layoutId}
                >
                  {layout.layoutName}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          )}

          {detailData.layouts.map((layout) => (
            <Tabs.Panel
              key={`${layout.layoutId}-panel`}
              value={layout.layoutId}
            >
              <Stack gap="md">
                <Table
                  section={layout.netRevenueSection}
                  view={renderView}
                  freezeStorageKey={layout.layoutId}
                  openOwnerStatementsModal={
                    renderOwnerStatementsTable
                      ? openOwnerStatementsModal
                      : undefined
                  }
                  onOpenJournalEntries={onOpenJournalEntries}
                />
                {layout.otherSections.map((section) => (
                  <Table
                    section={section}
                    view={renderView}
                    key={section.title ?? createUUID()}
                    onOpenJournalEntries={onOpenJournalEntries}
                  />
                ))}
                {layout.payoutsSection && (
                  <Table
                    section={layout.payoutsSection}
                    view={renderView}
                    onOpenJournalEntries={onOpenJournalEntries}
                  />
                )}
              </Stack>
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>
    </Box>
  );
};

type Column = MRT_ColumnDef<OwnerStatementSummariesDetailTableRow>;
type TableColumns = Column[];
type FreezeableColumn = Pick<Column, 'id' | 'accessorKey' | 'header'>;

const getFreezeColumnId = (column: FreezeableColumn) => {
  if (column.id) {
    return String(column.id);
  }

  if (column.accessorKey) {
    return String(column.accessorKey);
  }

  return typeof column.header === 'string' ? column.header : 'column';
};

function useColumnFreezing({
  columns,
  storageKey,
}: {
  columns: FreezeableColumn[];
  storageKey: string;
}) {
  const [{ id: teamId }] = useTeam();

  const [state, setState] = useLocalStorage<MRT_ColumnPinningState>({
    key: `owner-statement-summaries-detail-${teamId}-${storageKey}-${columns
      .map(getFreezeColumnId)
      .join('.')}`,
    defaultValue: {},
  });

  return useMemo(() => {
    return {
      pinned: state.left ?? [],
      setPinned: (id: string) => {
        setState((currentState) => {
          const left = currentState.left ?? [];

          return {
            ...currentState,
            left: left.includes(id)
              ? left.filter((currentId) => currentId !== id)
              : [...left, id],
          };
        });
      },
    };
  }, [setState, state]);
}

const Header = ({
  contact,
  team,
  monthRange,
  currency: initialCurrency,
  financials,
  listing,
  onOpenListing,
  onOpenPayout,
}: {
  listing: OwnerStatementSummariesDetailData['listing'];
  contact: OwnerStatementSummariesDetailData['contact'];
  team: OwnerStatementSummariesDetailData['team'];
  monthRange: `${string}...${string}`;
  financials: OwnerStatementSummariesDetailData['financials'];
  currency: OwnerStatementSummariesDetailData['currency'];
  onOpenListing?: (listingId: string) => void;
  onOpenPayout?: (payoutId: string) => void;
}) => {
  const [{ currency: teamCurrency }] = useTeam();
  const currency = initialCurrency || teamCurrency;

  const [dashboard] = useDashboard();

  const lines = useMemo(() => {
    const balanceForward = financials.balanceStart + financials.payouts;
    const reserve = financials.reserve ?? 0;

    return [
      {
        name: 'Balance start',
        text: formatCurrency(financials.balanceStart / 100, currency),
      },
      {
        name: 'Payouts',
        text: formatCurrency(financials.payouts / 100, currency),
      },
      {
        name: 'Balance forward',
        text: formatCurrency(balanceForward / 100, currency),
        props: {
          separated: true,
        },
      },
      {
        name: 'Net revenue',
        text: formatCurrency(financials.netRevenue / 100, currency),
      },
      {
        name: 'Expenses',
        text: formatCurrency(financials.expenses / 100, currency),
      },
      {
        name: 'Balance end',
        text: formatCurrency(financials.balanceEnd / 100, currency),
        props: {
          separated: true,
        },
      },
      ...(reserve !== 0
        ? [
            {
              name: 'Reserve',
              text: formatCurrency(reserve / 100, currency),
            },
            {
              name: 'Available balance',
              text: formatCurrency(financials.currentBalance / 100, currency),
              props: {
                separated: true,
              },
            },
          ]
        : []),
    ];
  }, [currency, financials]);

  return (
    <StatementSummaryHeader
      contact={contact}
      team={team}
      listing={listing}
      mode={dashboard === 'owner' ? 'owner' : 'propertyManager'}
      summaryTitle={ownerStatementSummariesMonthRangeLabelFormatter(monthRange)}
      lines={lines}
      payouts={[]}
      currency={currency}
      onOpenListing={onOpenListing}
      onOpenPayout={onOpenPayout}
    />
  );
};

const Table = memo(
  ({
    section,
    view,
    freezeStorageKey,
    openOwnerStatementsModal,
    onOpenJournalEntries,
  }: {
    section: OwnerStatementSummariesDetailTableSection;
    view: DetailFilter['view'];
    freezeStorageKey?: string;
    openOwnerStatementsModal?: (
      modalFilter: OwnerStatementsModalFilter
    ) => void;
    onOpenJournalEntries?: (
      args: OwnerStatementSummariesJournalEntriesArgs
    ) => void;
  }) => {
    const sectionColumns = useMemo(
      () =>
        getOwnerStatementSummariesDetailSectionColumns({
          section,
        }),
      [section]
    );
    const primaryColumnTitle = useMemo(
      () =>
        getOwnerStatementSummariesDetailPrimaryColumnTitle({
          columns: sectionColumns,
        }),
      [sectionColumns]
    );
    const visibleColumns = useMemo(
      () =>
        getOwnerStatementSummariesDetailVisibleColumns({
          columns: sectionColumns,
          primaryColumnTitle,
        }),
      [sectionColumns, primaryColumnTitle]
    );
    const firstColumnHeader = useMemo(
      () =>
        getOwnerStatementSummariesDetailFirstColumnHeader({
          primaryColumnTitle,
          sectionTitle: section.title,
          view,
        }),
      [primaryColumnTitle, section.title, view]
    );

    const columns = useMemo<TableColumns>(() => {
      const titleColumn: Column = {
        id: 'title',
        header: firstColumnHeader ?? toTitleCase(view),
        accessorFn: (row) =>
          getOwnerStatementSummariesDetailDisplayTitle({
            row,
            primaryColumnTitle,
          }),
      };

      const valueColumns = visibleColumns.map<Column>((column, _index) => {
        return {
          id: column.title,
          header: column.title,
          // accessorFn: (row) => row.columns?.[index]?.formatted,
          mantineTableBodyCellProps: { align: 'right', ta: 'right' },
          mantineTableHeadCellProps: { align: 'right', ta: 'right' },
          mantineTableFooterCellProps: {
            align: 'right',
            ta: 'right',
            pr: 'xs',
          },
          Cell: ({ row }) => (
            <TableCell
              row={row}
              columnTitle={column.title}
              view={view}
              onOpenJournalEntries={onOpenJournalEntries}
            />
          ),
          Footer: () => {
            const footers = section.subtotal?.columns ?? [];

            const footer = footers.find((f) => f.title === column.title);

            return footer?.formatted ?? null;
          },
        };
      });

      const base = [titleColumn, ...valueColumns];

      if (openOwnerStatementsModal) {
        base.splice(1, 0, {
          id: 'statementIds',
          header: 'Statements',
          Cell: ({ row }) => {
            const theme = useMantineTheme();

            const { filter } = useOwnerStatementSummariesDetailFilter();

            const statements = row.original.ownerStatements ?? [];
            const isEnabled = !!statements.length;

            return (
              <Tag
                size="sm"
                fontSize={12}
                radius="lg"
                color={isEnabled ? theme.colors.vrplatform[6] : undefined}
                onClick={() => {
                  const uniquePeriodIds = [
                    ...new Set(statements.map((x) => x.ownershipPeriodId)),
                  ];

                  if (isEnabled)
                    openOwnerStatementsModal({
                      listingIds: filter.listingIds?.join(','),
                      ownerIds: filter.contactId,
                      periodIds: uniquePeriodIds.join(',') || undefined,
                      // statementIds:
                      //   statements
                      //     .map((x) => x.id)
                      //     .filter(hasValue)
                      //     .join(',') || undefined,
                      dateRange: filter.monthRange,
                    });
                }}
              >
                {statements.length}{' '}
                {pluralize(statements.length, 'statement', 'statements')}
              </Tag>
            );
          },
        });
      }

      return base;
    }, [
      view,
      openOwnerStatementsModal,
      onOpenJournalEntries,
      firstColumnHeader,
      primaryColumnTitle,
      section.subtotal?.columns,
      visibleColumns,
    ]);

    const hideHeader = !section.rows?.length;

    const rows = section.rows ?? [];
    const columnOrder = useMemo(
      () => columns.map(getFreezeColumnId),
      [columns]
    );
    const freeze = useColumnFreezing({
      columns,
      storageKey: freezeStorageKey ?? section.title ?? 'section',
    });
    const showFreezeMenu = !!freezeStorageKey;

    return (
      <Box>
        <Collapse
          title={section.title || '-'}
          rightSection={
            showFreezeMenu ? (
              <ColumnFreezingMenu columns={columns} {...freeze} />
            ) : undefined
          }
        >
          <Box h={rows.length >= 10 ? 500 : undefined}>
            <LazyTable
              data={{
                rows,
                error: null,
                loading: false,
                rowCount: rows.length,
              }}
              table={{
                columns,
                columnOrder,
                memoMode: 'cells',
                hideTopBar: true,
                hideHeader,
                emptyRowsFallback: 'No rows to display',
                enableStickyFooter: true,
              }}
              pinned={showFreezeMenu ? freeze.pinned : undefined}
              virtualization={{
                enableRowVirtualization: true,
                overscan: 5,
              }}
            />
          </Box>
        </Collapse>
      </Box>
    );
  }
);

Table.displayName = 'OwnerStatementSummariesDetailTable';

const ColumnFreezingMenu = ({
  columns,
  pinned,
  setPinned,
}: {
  columns: FreezeableColumn[];
} & ReturnType<typeof useColumnFreezing>) => {
  const theme = useMantineTheme();

  return (
    <EllipsisMenu
      closeOnItemClick={false}
      width="auto"
      target={
        <ActionIcon aria-label="Freeze columns" color="gray" variant="subtle">
          <Icon icon="Grid06Icon" size={18} color={theme.colors.neutral[4]} />
        </ActionIcon>
      }
    >
      <ScrollArea.Autosize mah={400}>
        {columns.map((column) => {
          const id = getFreezeColumnId(column);
          const isPinned = pinned.includes(id);

          return (
            <EllipsisMenuItem
              key={id}
              rightSection={
                isPinned ? <Icon icon="CheckIcon" size={16} /> : null
              }
              onClick={() => setPinned(id)}
            >
              {typeof column.header === 'string' ? column.header : id}
            </EllipsisMenuItem>
          );
        })}
      </ScrollArea.Autosize>
    </EllipsisMenu>
  );
};

const TableCell = (props: {
  row: MRT_Row<OwnerStatementSummariesDetailTableRow>;
  columnTitle: string;
  view: DetailFilter['view'];
  onOpenJournalEntries?: (
    args: OwnerStatementSummariesJournalEntriesArgs
  ) => void;
}) => {
  const [dashboard] = useDashboard();
  const { filter: summaryFilter } = useOwnerStatementSummariesDetailFilter();

  const row = props.row.original;
  const cell = row.columns.find((column) => column.title === props.columnTitle);

  const formatted = cell?.formatted;

  const accountIds = cell?.accountIds ?? [];
  const rowId = row.id;

  const onClick = useMemo(() => {
    if (dashboard === 'owner' || !props.onOpenJournalEntries) {
      return undefined;
    }

    if (accountIds.length) {
      const params: OwnerStatementSummariesJournalEntriesArgs = {
        filter: {
          accountIds: accountIds.join(','),
          date: summaryFilter.monthRange,
          contactIds: summaryFilter.contactId,
          isDateRangeEndInclusive: true,
          party: 'owners',
        },
        // ownerStatementIds: row.statementIds, // do not include statementIds cause it could also include draft statements
      };
      const isUnassigned = !rowId || rowId.toLowerCase() === 'unassigned';

      if (props.view === 'listing') {
        params.filter!.listingIds = rowId;
      }

      if (props.view === 'month') {
        params.filter!.date = `${rowId}...${day(rowId).endOf('month').yyyymmdd()}`;
      }

      if (props.view === 'reservation') {
        if (isUnassigned) {
          return undefined;
        }

        params.filter!.reservationIds = rowId;
      }

      if (props.view === 'bookingChannel') {
        if (isUnassigned) {
          return undefined;
        }

        params.filter!.bookingChannels = rowId;
      }

      return () => {
        props.onOpenJournalEntries?.(params);
      };
    }

    return undefined;
  }, [
    accountIds,
    rowId,
    props.onOpenJournalEntries,
    summaryFilter.monthRange,
    props.view,
    summaryFilter.contactId,
    dashboard,
  ]);

  const sx = useMemo<EmotionSx | undefined>(() => {
    return onClick
      ? {
          cursor: 'pointer',
          transition: 'text-decoration 0.2s ease-in-out',
          ':hover': {
            textDecoration: 'underline',
          },
        }
      : undefined;
  }, [onClick]);

  return (
    <Text ta={'right'} sx={sx} onClick={onClick}>
      {formatted}
    </Text>
  );
};

const OwnerStatementsModal = ({
  modalFilter,
  closeModal,
  renderOwnerStatementsTable,
}: {
  modalFilter: OwnerStatementsModalFilter | undefined;
  closeModal: () => void;
  renderOwnerStatementsTable?: (
    modalFilter: OwnerStatementsModalFilter | undefined
  ) => React.ReactNode;
}) => {
  if (!renderOwnerStatementsTable) {
    return null;
  }

  return (
    <Modal
      size={'100%'}
      opened={!!modalFilter}
      onClose={closeModal}
      title="Owner Statements"
      // fullScreen
      styles={{
        title: {
          fontWeight: 600,
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          paddingTop: 0,
          height: '80dvh',
          minHeight: 600,
        },
      }}
    >
      {renderOwnerStatementsTable(modalFilter)}
    </Modal>
  );
};
